import { Alert, Button, Group, Modal, Stack } from "@mantine/core";
import { useCallback, type FC } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useTRPC } from "../../../../trpc";
import { captureEvent } from "../../../../utils/posthog";
import { Event } from "../../../../constants/events";
import type { Tileset, TilesetVersion } from "../types/tileset";
import { useMutation, useQueryClient } from "@tanstack/react-query";

interface DeleteVersionModalProps {
  open: boolean;
  onClose: () => void;
  tileset: Tileset;
  version: TilesetVersion;
}

export const DeleteVersionModal: FC<DeleteVersionModalProps> = ({
  onClose,
  open,
  tileset,
  version,
}) => {
  const {
    id: tilesetId,
    key: tilesetKey,
  } = tileset;

  const {
    id: versionId,
  } = version;

  const intl = useIntl();
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const { mutateAsync: deleteTilesetVersion, isPending } = useMutation(trpc.deleteTilesetVersion.mutationOptions());

  const handleDelete = useCallback(() => {
    captureEvent(Event.TilesetDeleteVersion);

    void deleteTilesetVersion({ tilesetId, versionId }).then(() => {
      void queryClient.invalidateQueries(trpc.getTilesets.queryFilter());
      void queryClient.invalidateQueries(trpc.getTilesetWithVersions.queryFilter());
      onClose();
    }).catch((error: unknown) => {
      logger.error({ error }, "Failed to delete tileset:");
    });
  }, [deleteTilesetVersion, tilesetId, onClose, queryClient]);

  return (
    <Modal
      opened={open}
      onClose={onClose}
      title={intl.formatMessage({ defaultMessage: "Delete tileset", id: "title.deleteTileset" })}
    >
      <Stack gap="xs">
        <FormattedMessage
          id="text.deleteTilesetVersionDescription"
          defaultMessage="Are you sure you want to delete the tileset {tileset} version {version}? This action cannot be undone."
          values={{ tileset: tilesetKey, version: versionId }}
        />

        <Alert variant="light" color="red" icon={<IconAlertTriangle />}>
          <FormattedMessage
            defaultMessage="Deleting this tileset version will remove it from your account and any maps using it may no longer display correctly."
            id="text.deleteTilesetVersionWarning"
          />
        </Alert>

        <Group gap="xs" justify="flex-end" pt="lg">
          <Button variant="secondary" type="button" loading={isPending} onClick={onClose}>
            <FormattedMessage defaultMessage="Cancel" id="button.cancel" />
          </Button>

          <Button variant="danger" type="button" loading={isPending} onClick={handleDelete}>
            <FormattedMessage defaultMessage="Delete" id="button.delete" />
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
